import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useContext
} from 'react';
import { string, bool, number, shape } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';

import { ProductPod } from '@thd-olt-component-react/product-pod';
import { QueryProvider } from '@thd-nucleus/data-sources';

import './SponsoredProductPod.scss';
import { triggerNewRelic } from '../../utils/helpers';
import { newRelicConstants } from '../../constants';

const SponsoredProductPod = ({
  product,
  storeId,
  position,
  schema,
  pageId,
  noATCFulfillment,
  dataSource = 'searchNav',
  targetingSource,
}) => {
  const [isSwatchContainerHidden, setIsSwatchContainerHidden] = useState(false);
  const [isSwatchMoreOptionsHidden, setIsSwatchMoreOptionsHidden] = useState(false);
  const [beacon, setBeacon] = useState('');
  const [linkBeacon, setLinkBeacon] = useState('');
  const [isProductClickHandlerAttached, setIsProductClickHandlerAttached] = useState(false);
  const [atcBeacon, setAtcBeacon] = useState('');
  const [atcButton, setAtcButton] = useState();
  const [isATCButtonCLickHandlerAttached, setIsATCButtonCLickHandlerAttached] = useState(false);

  const experienceContext = useContext(ExperienceContext);

  const isMobile = useMemo(() => (experienceContext.channel === 'mobile'), [experienceContext.channel]);

  useEffect(() => {
    const protocolSubstring = 'https:';
    let { onClickBeacon } = product;

    if (onClickBeacon.startsWith(protocolSubstring)) {
      onClickBeacon = onClickBeacon.slice(protocolSubstring.length);
    }

    setBeacon(onClickBeacon);
  }, [product.onClickBeacon]);

  useEffect(() => {
    const merchRec = `MERCH=REC-_-sp-_-${schema}-_-${position}-_-n/a-_-${pageId}-_-n/a-_-n/a-_-n/a`;
    /* eslint-disable-next-line */
    setLinkBeacon(`${beacon}${location.protocol}//${location.host}${product.canonicalURL}?${merchRec}&ITC=${product.ITC}`);
  }, [product, position, schema, pageId, beacon]);

  const handleProductClick = useCallback((evt) => {
    if (linkBeacon) {
      triggerNewRelic(`${newRelicConstants.CAROUSEL_PLA}-${targetingSource}`, newRelicConstants.CLICK);
      evt.preventDefault();
      window.location = linkBeacon;
    }
  }, [linkBeacon]);

  useEffect(() => {
    if (!isProductClickHandlerAttached) {
      const productLinksSelector = `[data-product-id="${product.id}"] a[href*="${product.id}"]`;
      const productLinks = document.querySelectorAll(productLinksSelector);

      if (productLinks.length) {
        productLinks.forEach((link) => {
          link.addEventListener('click', handleProductClick);
        });
        setIsProductClickHandlerAttached(true);
      }
    }
    return () => {
      if (isProductClickHandlerAttached) {
        const productLinksSelector = `[data-product-id="${product.id}"] a[href*="${product.id}"]`;
        const productLinks = document.querySelectorAll(productLinksSelector);

        if (productLinks.length) {
          productLinks.forEach((link) => {
            link.removeEventListener('click', handleProductClick);
          });
          setIsProductClickHandlerAttached(false);
        }
      }
    };
  });

  useEffect(() => {
    const redirectSubstring = '&redirect=';
    let onClickBeacon = beacon;

    if (onClickBeacon.endsWith(redirectSubstring)) {
      onClickBeacon = onClickBeacon.slice(0, -redirectSubstring.length);
    }

    setAtcBeacon(onClickBeacon);
  }, [beacon]);

  useEffect(() => {
    if (!atcButton) {
      const atcButtonSelector = `[data-product-id="${product.id}"] button[class*="add-to-cart"]`;
      setAtcButton(document.querySelectorAll(atcButtonSelector)[0]);
    }
  });

  useEffect(() => {
    if (atcButton && atcBeacon) {
      const atcButtonClickHandler = () => fetch(atcBeacon);

      if (!isATCButtonCLickHandlerAttached) {
        atcButton.addEventListener('click', atcButtonClickHandler);
        setIsATCButtonCLickHandlerAttached(true);
      }
    }
  }, [product, atcBeacon, atcButton]);

  useEffect(() => {
    const addBeaconingOnATCButtonClick = () => {
      const atcBtn = document.querySelector(`[data-product-id="${product.id}"] button[class*="add-to-cart"]`);
      if (!isATCButtonCLickHandlerAttached && atcBtn) {
        atcBtn.addEventListener('click', () => fetch(atcBeacon));
        setIsATCButtonCLickHandlerAttached(true);
      }
    };
    window.addEventListener('load', addBeaconingOnATCButtonClick);
    return () => window.removeEventListener('load', addBeaconingOnATCButtonClick);
  }, [product.id, atcBeacon, isATCButtonCLickHandlerAttached]);

  useEffect(() => {
    if (!isSwatchContainerHidden) {
      const swatchContainerSelector = `[data-product-id="${product.id}"] div[class*="swatch-container"]`;
      const swatchContainer = document.querySelector(swatchContainerSelector);

      if (swatchContainer) {
        swatchContainer.parentElement.removeChild(swatchContainer);
        setIsSwatchContainerHidden(true);
      }
    }
  });

  useEffect(() => {
    if (!isSwatchMoreOptionsHidden) {
      const swatchMoreOptionsSelector = `[data-product-id="${product.id}"]
      a[class*="super-sku__inline-swatch__mini-swatch__more-options"]`;
      const swatchMoreOptions = document.querySelector(swatchMoreOptionsSelector);

      if (swatchMoreOptions) {
        swatchMoreOptions.parentElement.removeChild(swatchMoreOptions);
        setIsSwatchMoreOptionsHidden(true);
      }
    }
  });

  return (
    <QueryProvider
      cacheKey="sponsored-product-pod"
      dataSource={dataSource}
      defaultVariables={{ storeId }}
    >
      <div
        data-product-id={product.id}
        data-onview-beacon-nucleus={product.onViewBeacon}
        data-onclick-beacon={product.onClickBeacon}
        className="sponsored-product-pod"
        data-testid="sponsored-product-pod"
      >
        <ProductPod
          itemId={product?.id?.toString()}
          storeId={storeId}
          brandTitleMaxLine={3}
          hideFulfillment
          hideShopThisCollection
          noATCFulfillment={noATCFulfillment}
          hideATC={isMobile}
          hideFavorites
          hideBadges
          hidePriceBadge
          doNotShowChooseYourOptions
        />
      </div>
    </QueryProvider>
  );
};

SponsoredProductPod.propTypes = {
  dataSource: string,
  storeId: string.isRequired,
  product: shape({
    id: number.isRequired,
    onClickBeacon: string.isRequired,
    onViewBeacon: string.isRequired
  }).isRequired,
  position: number.isRequired,
  schema: string.isRequired,
  pageId: string.isRequired,
  noATCFulfillment: bool,
  targetingSource: string,
};

SponsoredProductPod.defaultProps = {
  noATCFulfillment: null,
  dataSource: 'searchNav',
  targetingSource: 'none',
};

export { SponsoredProductPod };
